import bind from 'bind-decorator';
import { PAGINATION_OPTIONS } from 'global/constants';
import * as React from 'react';

import { Icon, PaginationSizeOptions, ResultsCount, SortOptions, Tag } from 'ui/components';
import { WrappedMessage } from 'utils';
import messages from '../../displayMessages';
import Skeleton from 'react-loading-skeleton';
import { intl } from 'i18n';

interface CardsListTopBarProps {
    isMobileView: boolean;
    isTabletView: boolean;
    filterShown: boolean;
    onClear: () => void;
    /** Event to call when a keyword filter (entered by the user in the search box) is removed */
    onClearFilter: (query: string) => void;
    /** Event to call when a tag filter (e.g. a specific Subject Area) is removed */
    onClearTag: (tag: string) => void;
    onSizeSelect: (paginationSize: number) => void;
    onSortSelect: (sortOrder: string) => void;
    /** In mobile view only, this event indicates the user wants to hide/show the list of tag filter options */
    onToggleFilter: () => void;
    paginationSize: number;
    resultCount: number;
    /** Set of keyword search terms/filters that are applied */
    searchQueries: ReadonlySet<string>;
    sortOrder: string;
    sortOrderOptions: { label: string, value: string}[];
    /** Set of tag filters (e.g. a specific subject area) that are applied */
    tags: ReadonlyArray<{id: string, name: string}>;
    /** Allow filter buttons to be shown or not */
    allowFiltering?: boolean;

    showSkeleton?: boolean;
}

/**
 * The CardsListTopBar displays the number of pages in a paginated list of Cards.
 *
 * It also displays option dropdowns for number of pages and sort order.
 */
export class CardsListTopBar extends React.Component<CardsListTopBarProps> {

    public render() {
        const paginationSizeOptions = <PaginationSizeOptions
            name='cards_pagination_size_options'
            value={this.props.paginationSize}
            options={PAGINATION_OPTIONS}
            onChange={this.props.onSizeSelect}
        />;

        const sortOptions = <SortOptions
            name='cards_sort_options'
            value={this.props.sortOrder}
            options={this.props.sortOrderOptions}
            onChange={this.props.onSortSelect}
            isMobileView={this.props.isMobileView}
        />;

        const filters = (this.props.searchQueries.size > 0 || this.props.tags.length > 0) ? (
            <div className='cards-list-top-bar-row-2'>
                {this.props.showSkeleton ?
                    <Skeleton />
                    : <>
                    {
                        Array.from(this.props.searchQueries).map(((keyword) =>
                            <span className='keyword' key={keyword}>
                                Keyword: {keyword}
                                <button className={`${'btn'} ${'btn-link'} ${'clear-button'}`}
                                        onClick={() => {this.removeFilter(keyword); }}>
                                    <span className='sr-only'>
                                        <WrappedMessage message={messages.removeSearchCriteria} />
                                    </span>
                                    <Icon name='x' zoom='0.8em'/>
                                </button>
                            </span>
                        ))
                    }

                    {
                        this.props.tags.map(((tag) =>
                            <Tag key={tag.id} onClick={() => {this.removeTag(tag.id); }}>
                                {tag.name}
                                <span className='sr-only'>
                                    <WrappedMessage message={messages.removeSearchCriteria} />
                                </span>
                            </Tag>
                        ))
                    }

                    <button className='btn clear-all' onClick={this.onClear}>
                        <WrappedMessage message={messages.filterClear}/>
                    </button>
                </>}
            </div>
        ) : undefined;

        const isExclusiveTabletView = !this.props.isMobileView && this.props.isTabletView;
        const isDesktopView = !this.props.isMobileView && !this.props.isTabletView;
        // Show results: on desktop and in modals when the filters are hidden.
        const showResultsCount = isDesktopView || (this.props.isTabletView && !this.props.filterShown);

        return (
            <div className='cards-list-top-bar'>
                <div className='cards-list-top-bar-row-1 d-flex'>
                    { (isDesktopView || this.props.isTabletView) && this.props.showSkeleton ? <Skeleton />
                      : showResultsCount &&
                            <div className='result-count mr-auto'>
                                <ResultsCount totalCount={this.props.resultCount}/>
                            </div>
                    }
                    <div className={`result-controls${this.props.isMobileView ? 'result-controls-mobile' : ''}`}>
                        {this.props.showSkeleton ? <Skeleton /> : <>
                            {!this.props.isMobileView &&
                                <div className='pagination-size-options'>{ paginationSizeOptions }</div>
                            }
                            <div className='options-container'>{ sortOptions }</div>
                        </>}
                    </div>
                    {
                        (!this.props.showSkeleton && (this.props.isMobileView || this.props.isTabletView)) &&
                        this.props.allowFiltering &&
                        <>
                            <div className='options-container'>
                                <button
                                    className={`btn filter-toggle ${isExclusiveTabletView ? ' is-tablet-view' : ''}`}
                                    onClick={this.onToggleFilter}
                                    title={intl.formatMessage(messages.filterToggle)}
                                >
                                    <Icon name='settings' zoom='1.4em' />
                                    {isExclusiveTabletView &&
                                        <span><WrappedMessage message={messages.filterToggle} /></span>
                                    }
                                </button>
                            </div>
                        </>
                    }
                    { this.props.isMobileView &&
                        <div className='result-count mr-auto'>
                            <ResultsCount totalCount={this.props.resultCount}/>
                        </div>
                    }
                </div>
                { filters }
            </div>
        );
    }

    @bind private onToggleFilter(event: React.MouseEvent<HTMLButtonElement>): void {
        event.preventDefault();
        this.props.onToggleFilter();
    }

    @bind private onClear(event: React.MouseEvent<HTMLButtonElement>): void {
        event.preventDefault();
        this.props.onClear();
    }

    @bind private removeFilter(keyword: string) {
        this.props.onClearFilter(keyword);
    }

    @bind private removeTag(tagId: string) {
        this.props.onClearTag(tagId);
    }
}
